/**
 * BPF Cart Picture Frame — Phase 4D.10D.
 *
 * Extends the cart memorial preview (snippets/bpf-cart-memorial-
 * preview.liquid) with the Picture Frame add-on's frame + placeholder-
 * photo layers, in the AJAX cart drawer and the full cart page. Mirrors
 * assets/bpf-cart-engraving.js's exact architecture (per-canvas cached
 * state via WeakMap, renderOne/renderAll, a debounced MutationObserver
 * on the same two containers) — this file owns exactly one thing:
 * finding `[data-cart-picture-frame]` canvas elements and calling the
 * EXISTING shared compositing primitive with data read from them and
 * from the shared [data-picture-frame-profiles] JSON. It contains no
 * drawing logic, no aperture geometry, and no filename conventions of
 * its own — all of that lives in assets/bpf-memorial-renderer.js
 * (window.BpfMemorialRenderer, the same module the Builder's live
 * preview and PNG export both already use) and in the profiles JSON
 * (snippets/bpf-picture-frame-data.liquid, embedded once per page —
 * see snippets/cart-drawer.liquid / sections/main-cart-items.liquid).
 *
 * Case C only (Phase 4D.10D audit finding): a real customer photo
 * cannot be resolved from Liquid alone (no generic accessor exists for
 * turning a stored Shopify File GID into a URL), so this file only ever
 * draws the orientation-specific placeholder "Your Pet's Photo Here"
 * art into the aperture — never a real photo, never anything read from
 * or written to _Memorial Photo File GID/pet_photo. The frame <img>
 * itself is a plain, already-resolved-by-Liquid image and needs no JS
 * at all; this file only ever draws onto the frame-photo canvas layered
 * above it.
 *
 * WHEN this runs: identical triggers to assets/bpf-cart-engraving.js —
 * once on page load, and again whenever Shopify's existing AJAX cart
 * flow rebuilds the drawer or cart-page item list (a childList
 * MutationObserver on #CartDrawer / #main-cart-items, debounced 50ms).
 */

if (typeof window.BpfCartPictureFrame === 'undefined') {
  window.BpfCartPictureFrame = (function () {
    'use strict';

    // Parsed once and cached — the SAME [data-picture-frame-profiles]
    // JSON sections/bpf-memorial-designer.liquid already embeds for the
    // Builder, now also embedded on cart pages (snippets/bpf-picture-
    // frame-data.liquid is the single shared source for both).
    let profilesData = null;
    let profilesParsed = false;
    function getProfiles() {
      if (profilesParsed) return profilesData;
      profilesParsed = true;
      const script = document.querySelector('[data-picture-frame-profiles]');
      if (!script) return null;
      try {
        profilesData = JSON.parse(script.textContent);
      } catch (error) {
        profilesData = null;
      }
      return profilesData;
    }

    // canvasEl -> in-flight/last-drawn signature, so a canvas that
    // survives across two renderAll() calls (nothing rebuilt it, or an
    // unrelated cart line changed) never redraws itself redundantly.
    const drawn = new WeakMap();

    function clearCanvas(canvasEl) {
      if (typeof canvasEl.getContext !== 'function') return;
      const ctx = canvasEl.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    }

    async function renderOne(canvasEl) {
      const renderer = window.BpfMemorialRenderer;
      if (!renderer || typeof renderer.loadImage !== 'function' || typeof renderer.compositePictureFramePhoto !== 'function') {
        return;
      }

      const data = canvasEl.dataset;
      const orientationKey = (data.pictureFrameOrientation || '').toLowerCase();
      const frameSrc = data.pictureFrameFrameSrc || '';
      if (!orientationKey || !frameSrc) {
        canvasEl.hidden = true;
        return;
      }

      // Signature dedup: an unchanged frame src + orientation on an
      // already-drawn canvas means nothing about this line changed —
      // skip the redundant reload/redraw (mirrors the Builder's own
      // signature-based dedup elsewhere in this project).
      const signature = orientationKey + '|' + frameSrc;
      if (drawn.get(canvasEl) === signature) return;

      const profiles = getProfiles();
      const profile = profiles && profiles[orientationKey];
      const placeholderSrc = profile && profile.placeholderAsset;
      if (!profile || !placeholderSrc) {
        canvasEl.hidden = true;
        return;
      }

      try {
        const [frameImg, placeholderImg] = await Promise.all([
          renderer.loadImage(frameSrc),
          renderer.loadImage(placeholderSrc),
        ]);

        clearCanvas(canvasEl);
        const ctx = canvasEl.getContext('2d');
        const box = { x: 0, y: 0, width: canvasEl.width, height: canvasEl.height };
        renderer.compositePictureFramePhoto(ctx, frameImg, box, profile.aperture, placeholderImg, profile.placeholderContentBounds);
        canvasEl.hidden = false;
        drawn.set(canvasEl, signature);
      } catch (error) {
        // Frame or placeholder failed to load — the frame <img> beside
        // this canvas still shows on its own; never throw into the
        // MutationObserver callback over this decorative add-on layer.
        canvasEl.hidden = true;
      }
    }

    function renderAll() {
      document.querySelectorAll('[data-cart-picture-frame]').forEach(renderOne);
    }

    let scheduled = null;
    function scheduleRenderAll() {
      if (scheduled) return;
      scheduled = setTimeout(() => {
        scheduled = null;
        renderAll();
      }, 50);
    }

    function observe(container) {
      if (!container) return;
      const observer = new MutationObserver(scheduleRenderAll);
      observer.observe(container, { childList: true, subtree: true });
    }

    function init() {
      renderAll();
      observe(document.getElementById('CartDrawer'));
      observe(document.getElementById('main-cart-items'));
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    return { renderAll };
  })();
}
